/**
 * 10.1 price (scoring.valuePrice@2.0): one price function for display, buybacks, issuance, the bid and scoring. It advances the price model one
 * month from the state the bank carries (`valuation`, `rotceHistoryRate`, `balanceHistoryUsd`); the round-end price is the bank's stored `valuation`, never a second call.
 *   10.1 E1  TBV_req = ρ_req · RWA                        10.1 E7  G₃ = (B_m ÷ B_{m−L})^(12/L) − 1; g = clamp(g₀ + κ_g · (G₃ − g_tr), g₀ ± b_g)
 *   10.1 E2  X = CET1 − TBV_req, Excess, Shortfall        10.1 E8  μ*(c) = clamp((ROTCE_n − g) ÷ max(c − g, f), 0, μ_max)
 *   10.1 E3  Marks (after-tax AOCI, HTM, swap MTM)        10.1 E9  Xval = Excess − Shortfall ÷ (1 − δ(Shortfall))
 *   10.1 E4  SecAdj (securities at their market yield)    10.1 E10 μ_m = μ_{m−1} + λ · (μ*₀ − μ_{m−1})
 *   10.1 E5  NI_core, retained capital (marked) at the rate it earns  10.1 E11 V = (μ_m − (μ*₀ − μ*₁)) · TBV_req + Xval + Marks
 *   10.1 E6  ROTCE_f, ROTCE_n                             10.1 E12 P = max(V · (1 + Σ s_a δ_a), φ · (CET1 + Marks)) ÷ sh
 * COE is 10.2 (cost-of-equity.ts); NormProv is 10.3 E1 (norm-prov.ts).
 */
import { AFS_BOOKS, DEPOSIT_BOOKS, HTM_BOOKS, InvariantViolation, LOAN_BOOKS, roundToUsd, type Bp, type BankState, type BookId, type ContentParams, type QuarterRecord, type Rate, type Usd, type UsdPerShare, type ValuationBreakdown, type ValuationInput } from "@bsim/domain";
import { decayedAdjustment } from "./adjustments";
import { capitalKRate, coeOf, premiaBp } from "./cost-of-equity";
import { normalizedProvisionUsd } from "./norm-prov";
import { trailingFourQuarters } from "./ratios";

const TRAILING_QUARTERS = 4;
type ValuationParams = ContentParams["valuation"];
type IssuanceRule = ContentParams["capitalPolicy"]["issuance"];
export const SECURITIES_BOOKS = [...AFS_BOOKS, ...HTM_BOOKS] as const;

const MONTHS_PER_YEAR = 12;
const MONTHS_PER_QUARTER = 3;
const BORROWING_BOOKS: readonly BookId[] = ["fhlb", "ffp", "dw"];

/** 10.1 E8: the fair multiple of required capital, (ROTCE − g) ÷ (c − g) held to [0, μ_max] with c − g at least f. */
export const fairMultiple = (rotceRate: number, gRate: number, coeRate: number, params: ValuationParams): number =>
  Math.min(params.ptbvMax, Math.max(0, (rotceRate - gRate) / Math.max(coeRate - gRate, params.coeMinusGFloorRate)));

/** 10.1 E7: growth moves with the balance sheet's own growth against trend, half of the difference, inside the band. */
export const growthRateOf = (ownGrowthRate: number, params: ValuationParams): number => {
  const { growthBaseRate: g0, growthTrendRate, growthPassThroughRate, growthBandRate } = params.priceModel;
  return Math.min(g0 + growthBandRate, Math.max(g0 - growthBandRate, g0 + growthPassThroughRate * (ownGrowthRate - growthTrendRate)));
};

/**
 * 10.1 E9: what a capital shortfall costs the holders, S ÷ (1 − δ(S)), δ the 08.2 E7 raise discount at the market value the raise sells into (absent: the base discount).
 * S is the raise the market expects (`forcedRaiseUsd`), not the whole gap to required capital.
 */
export function shortfallCostUsd(shortfallUsd: number, marketValueUsd: number | undefined, issuance: IssuanceRule): number {
  if (shortfallUsd <= 0) return 0;
  const slope = marketValueUsd === undefined ? 0 : (issuance.discountSlope * shortfallUsd) / marketValueUsd;
  return shortfallUsd / (1 - (issuance.discountBaseRate + slope));
}

/**
 * 10.1 E9 (E2 item 7): the raise that thin capital forces, the CET1 missing from the level below which the market expects a raise (T₂, the supervisory capital-component line,
 * `premia.capital.threshold2Rate`). Between that level and required capital the gap is carried at face (a negative X) and the capital premium (10.2 E2) charges it; dilution is charged only
 * where a raise is expected, so one thin dollar is not charged twice.
 */
export function forcedRaiseUsd(cet1Usd: number, rwaUsd: number, params: ValuationParams): number {
  return Math.max(0, params.premia.capital.threshold2Rate * rwaUsd - cet1Usd);
}

/** 10.1 E9: Xval = Excess − Shortfall − (what the forced raise dilutes beyond its face): the gap carried at face, plus the discount a raise sells at. */
export function capitalValueUsd(cet1Usd: number, rwaUsd: number, params: ValuationParams, marketValueUsd: number | undefined, issuance: IssuanceRule): number {
  const raiseUsd = forcedRaiseUsd(cet1Usd, rwaUsd, params);
  return cet1Usd - params.requiredCapitalRate * rwaUsd - (shortfallCostUsd(raiseUsd, marketValueUsd, issuance) - raiseUsd);
}

/** B: loans + deposits, gross balances (10.1 E7). */
export function loansAndDepositsUsd(bank: BankState): number {
  let total = 0;
  for (const book of LOAN_BOOKS) for (const v of bank.books[book].vintages) total += v.balanceUsd;
  for (const book of DEPOSIT_BOOKS) for (const v of bank.books[book].vintages) total += v.balanceUsd;
  return total;
}

/** 10.1 E3: AOCI (AFS, swaps) plus the after-tax unrealized HTM mark. */
function marksUsd(bank: BankState, taxRate: number): number {
  let htmMarkUsd = 0;
  for (const book of HTM_BOOKS) for (const v of bank.books[book].vintages) htmMarkUsd += (v.fairValueUsd ?? v.balanceUsd) - v.balanceUsd;
  return bank.equity.aociAfsUsd + bank.equity.aociSwapUsd + (1 - taxRate) * htmMarkUsd;
}

/** The securities books priced off the MBS current coupon (`EnvSnapshot.mbsYieldRate`): a pool bought now yields it. */
const CURRENT_COUPON_BOOKS: readonly BookId[] = ["mbs_afs", "mbs_htm"];

/**
 * 10.1 E4: Σ over securities vintages of y_v · FV − B · c (a book with no market yield is a wiring defect, not a default). With `priceModel.securitiesReference`
 * "optionAdjusted" (E4′) y_v is one yield for every lot of the book, whatever its age, origin month or coupon, so selling a lot at its mark and rebuying the same
 * fair value changes nothing. Key absent or "coupon" (E4.4): a lot the bank inherited keeps the book reference (the SimStart marks stand) and a pool bought since
 * reads the MBS current coupon. With `priceModel.securitiesOptionCostBp` set (E13, OR-27.2) o applies to every MBS lot in both readings: the lot reads the
 * current-coupon yield less o, except an E4.4 pool bought since, which reads the current-coupon yield. Treasuries keep the book reference. Key absent: the book reference.
 * Also the new-purchase core yield r_e (E5): what a dollar bought now earns in the core, each book's yield for a pool bought since SimStart weighted by the fair value the bank holds
 * (the standing mix at SimStart, which the make-up it buys follows); undefined where the bank holds none.
 */
function securitiesReading(bank: BankState, yieldByBook: ValuationInput["marketYieldRateByBook"], env: ValuationInput["env"], priceModel: ValuationParams["priceModel"]): { adjustmentUsd: number; purchaseYieldRate: number | undefined } {
  const optionAdjusted = priceModel.securitiesReference === "optionAdjusted";
  const optionCostRate = priceModel.securitiesOptionCostBp === undefined ? undefined : priceModel.securitiesOptionCostBp / 1e4;
  let adjustmentUsd = 0, heldUsd = 0, purchaseIncomeUsd = 0;
  for (const book of SECURITIES_BOOKS) {
    const y = yieldByBook[book];
    if (y === undefined) throw new InvariantViolation("VALUATION_INPUT", `no market yield for securities book "${book}"`, { seatId: bank.seatId, month: bank.month });
    const mbs = CURRENT_COUPON_BOOKS.includes(book);
    const ySeasoned = mbs && optionCostRate !== undefined ? env.mbsYieldRate - optionCostRate : y;
    const yNew = !mbs ? y : optionAdjusted ? ySeasoned : env.mbsYieldRate;
    for (const v of bank.books[book].vintages) {
      const fairValueUsd = v.fairValueUsd ?? v.balanceUsd;
      adjustmentUsd += (v.originMonth > 0 ? yNew : ySeasoned) * fairValueUsd - v.balanceUsd * v.couponRate;
      heldUsd += fairValueUsd;
      purchaseIncomeUsd += yNew * fairValueUsd;
    }
  }
  return { adjustmentUsd, purchaseYieldRate: heldUsd > 0 ? purchaseIncomeUsd / heldUsd : undefined };
}

/**
 * The wholesale lines surplus cash repays (06.1 E8): the floating-rate (overnight) lots of the borrowing books, read as the mean of the quarter-ends X̄₄ is taken over (E2 item 8): a
 * quarter record carries each book's balance, not its lots, so a record's line is its book balance at the floating share the book holds today. A record without books (the
 * pre-SimStart quarters) reads today's line. Term lots and brokered CDs roll to maturity and are not repaid early.
 */
function repayableWholesaleUsd(bank: BankState, products: ValuationInput["products"], records: readonly QuarterRecord[]): number {
  const floating = new Set(products.filter((p) => p.rateType === "floating").map((p) => p.id));
  let today = 0;
  const share = new Map<BookId, number>();
  for (const book of BORROWING_BOOKS) {
    let floatingUsd = 0, totalUsd = 0;
    for (const v of bank.books[book].vintages) { totalUsd += v.balanceUsd; if (floating.has(v.productId)) floatingUsd += v.balanceUsd; }
    today += floatingUsd;
    share.set(book, totalUsd > 0 ? floatingUsd / totalUsd : 0);
  }
  if (records.length === 0) return today;
  let sum = 0;
  for (const q of records) {
    if (q.booksUsd === undefined) { sum += today; continue; }
    let line = 0;
    for (const book of BORROWING_BOOKS) line += (q.booksUsd[book] ?? 0) * (share.get(book) ?? 0);
    sum += line;
  }
  return sum / records.length;
}

/**
 * 10.1 E5: the income the capital above (below) required earns in the reported PPNR and the securities reading, which the core takes out so it is the return on required capital. A dollar of
 * surplus capital first repays the overnight wholesale line it can (06.1 E8) and so earns the marginal funding rate r_x; the rest is invested at the new-purchase core yield r_e. Missing capital
 * is met by the securities the bank does not hold (06.3 E6 sells the residual before borrowing), so it takes out r_e too. Where the bank holds no securities, r_e = r_x.
 */
function retainedCapitalIncomeUsd(xUsd: number, repayableUsd: number, fundingRate: number, purchaseYieldRate: number | undefined): number {
  const purchaseRate = purchaseYieldRate ?? fundingRate;
  if (xUsd <= 0) return (repayableUsd > 0 ? fundingRate : purchaseRate) * xUsd; // missing capital is one more dollar on the overnight line while the bank has one (reviews of 23 Oct), else a security not held
  const repaidUsd = Math.min(xUsd, repayableUsd);
  return fundingRate * repaidUsd + purchaseRate * (xUsd - repaidUsd);
}

/** 10.1 E6: ROTCE_n = w_f · ROTCE_f + (1 − w_f) · the mean of the last `rotceHistoryMonths` values, this month's included. */
function normalisedRotceRate(rotceForwardRate: number, history: readonly number[] | undefined, params: ValuationParams): number {
  const { rotceForwardWeightRate: w, rotceHistoryMonths: n } = params.priceModel;
  const prior = history === undefined ? [] : history.slice(-(n - 1));
  let sum = rotceForwardRate;
  for (const r of prior) sum += r;
  return w * rotceForwardRate + (1 - w) * (sum / (prior.length + 1));
}

/** 10.1 E7: the balance sheet's own annualised growth over `growthLookbackMonths`; trend where the bank carries no history. */
function ownGrowthRate(balanceUsd: number, history: readonly number[] | undefined, params: ValuationParams): number {
  const { growthLookbackMonths: lookback, growthTrendRate } = params.priceModel;
  const base = history !== undefined && history.length >= lookback ? (history[history.length - lookback] as number) : undefined;
  return base === undefined || base <= 0 ? growthTrendRate : (balanceUsd / base) ** (MONTHS_PER_YEAR / lookback) - 1;
}

/**
 * 10.1 E5 (OR-30.66): the capital the four quarters of PPNR₄ earned on, as the time-average of the signed excess X over those quarters: the mean of each quarter's opening and closing X (the
 * quarter-end before the first of the four is the opening). A quarter's income accrues across the quarter, so the quarter-end alone leads a pile that is building (the strip takes out more
 * than the pile earned) and trails one that is running down (less), which read as a lower, then a higher, core on the same bank. With fewer than five records the quarter-ends stand; with none, today's X.
 */
function timeAveragedExcessUsd(bank: BankState, params: ValuationParams): number {
  const xOf = (q: QuarterRecord): number => q.cet1Usd - params.requiredCapitalRate * q.rwaUsd;
  const records = bank.quarters.slice(-(TRAILING_QUARTERS + 1));
  if (records.length === 0) return bank.capital.cet1Usd - params.requiredCapitalRate * bank.capital.rwaUsd;
  if (records.length <= TRAILING_QUARTERS) return records.reduce((t, q) => t + xOf(q), 0) / records.length;
  let sum = 0;
  for (let i = 1; i < records.length; i += 1) sum += (xOf(records[i - 1] as QuarterRecord) + xOf(records[i] as QuarterRecord)) / 2;
  return sum / TRAILING_QUARTERS;
}

export function valuePrice(input: ValuationInput): ValuationBreakdown {
  const { bank, params, taxRate } = input;
  const pm = params.priceModel;
  const tbvRequiredUsd = params.requiredCapitalRate * bank.capital.rwaUsd;                    // E1
  const xUsd = bank.capital.cet1Usd - tbvRequiredUsd;                                         // E2
  const excessUsd = Math.max(0, xUsd), shortfallUsd = Math.max(0, -xUsd);
  // E5: PPNR₄ earned the capital the bank carried over those four quarters, so X is taken over the same quarter-ends (signed); with no record yet, today's X.
  const trailing = trailingFourQuarters(bank).records;
  let ppnr4Usd = 0, cdi4Usd = 0;
  // E5a: with `priceModel.oneOffOpex` "excluded" the one-time opex (event hits, restructuring charge, lease-exit charge) is added back, so it is a capital step once and never an earnings run-rate; absent or "inCore" the core is as it was.
  const oneOffAddBack = pm.oneOffOpex === "excluded";
  // E4a SwapAdj: reported PPNR less the live swaps' net interest is the core's PPNR (ADR-0067); the one place NI_core, ROTCE_f, ROTCE_n and (through the shadow months) ROTCE_LT are formed.
  for (const q of trailing) { ppnr4Usd += (q.ppnrUsd ?? 0) - (q.swapInterestUsd ?? 0) + (oneOffAddBack ? (q.oneOffOpexUsd ?? 0) + (q.lapsingOverrideUsd ?? 0) : 0); cdi4Usd += q.cdiAmortizationUsd ?? 0; }
  // E5 (OR-30.23): the strip is on the capital at its fair value, X̄₄ + the AFS marks, because E4 reads the securities at their market yield; the quarter record carries no AOCI (a contract wall, logged), so today's stands for each quarter-end.
  const xTrailingUsd = timeAveragedExcessUsd(bank, params) + bank.equity.aociAfsUsd;
  const normProvUsd = normalizedProvisionUsd(bank, input.products, params, input.lossMultByVintage);
  const securities = securitiesReading(bank, input.marketYieldRateByBook, input.env, params.priceModel);
  // E5b (OR-30.56 launch pass, the N3 carry): the core prices the wholesale share, not its tenor. Every overnight dollar is costed at the term wholesale rate r_w (the book's term product, `marketYieldRateByBook.fhlb`), so
  // funding short earns the saving in the reported PPNR while it lasts and the multiple never capitalises it; a surplus dollar that repays a line therefore saves r_w in the core (Ret below). Absent r_w: r_x, no adjustment.
  const overnightUsd = repayableWholesaleUsd(bank, input.products, trailing);
  const termRate = input.termWholesaleRate ?? input.marketYieldRateByBook.fhlb ?? input.retainedCapitalRate; // CP-2 v2.3 field; the `fhlb` yield key is the stop-gap it replaces (ADR-0094)
  const tenorAdjUsd = overnightUsd * (termRate - input.retainedCapitalRate);
  const retainedIncomeUsd = retainedCapitalIncomeUsd(xTrailingUsd, overnightUsd, termRate, securities.purchaseYieldRate);
  const niCoreUsd = (1 - taxRate) * (ppnr4Usd - tenorAdjUsd + securities.adjustmentUsd - normProvUsd + cdi4Usd - retainedIncomeUsd); // E5
  const rotceForwardRate = niCoreUsd / tbvRequiredUsd;
  const rotceNormRate = normalisedRotceRate(rotceForwardRate, bank.rotceHistoryRate, params);  // E6
  const balancesUsd = loansAndDepositsUsd(bank);
  const gRate = growthRateOf(ownGrowthRate(balancesUsd, bank.balanceHistoryUsd, params), params); // E7
  const premia = premiaBp(bank, params, input.horizonFinal ?? false, input.dividendPremiumQuarters, input.heldPremiaBp);
  const coe = coeOf(params, premia);
  const ptbvFair = fairMultiple(rotceNormRate, gRate, coe.standingRate, params);              // E8
  const ptbvFairWithEvents = fairMultiple(rotceNormRate, gRate, coe.rate, params);
  const lambda = 1 - (1 - pm.gapClosedPerQuarterRate) ** (1 / MONTHS_PER_QUARTER);
  const priorMultiple = bank.valuation?.ptbvRequired;
  const ptbvMarket = priorMultiple === undefined ? ptbvFair : priorMultiple + lambda * (ptbvFair - priorMultiple); // E10
  const priorPrice = bank.kpis.price;
  const xvalUsd = capitalValueUsd(bank.capital.cet1Usd, bank.capital.rwaUsd, params, priorPrice === undefined ? undefined : priorPrice * bank.sharesOutstanding, input.issuance); // E9
  const marks = marksUsd(bank, taxRate);
  const valueUsd = (ptbvMarket - (ptbvFair - ptbvFairWithEvents)) * tbvRequiredUsd + xvalUsd + marks; // E11
  const shockedUsd = valueUsd * (1 + decayedAdjustment(bank, "priceShockRate"));
  const floorUsd = pm.floorPtbv * (bank.capital.cet1Usd + marks);
  const priceUsdPerShare = Math.max(shockedUsd, floorUsd) / bank.sharesOutstanding;            // E12
  return {
    tbvRequiredUsd: roundToUsd(tbvRequiredUsd), excessUsd: roundToUsd(excessUsd), shortfallUsd: roundToUsd(shortfallUsd), marksUsd: roundToUsd(marks),
    niCoreUsd: roundToUsd(niCoreUsd), rotceCoreRate: rotceForwardRate as Rate, gRate: gRate as Rate, coeRate: coe.rate as Rate,
    premiaBp: premia as { [K in keyof ValuationBreakdown["premiaBp"]]: Bp }, capitalRecordRate: capitalKRate(bank, params, input.horizonFinal ?? false) as Rate,
    ptbvRequired: ptbvMarket, rotceNormRate: rotceNormRate as Rate, ptbvFair, ptbvFairWithEvents, xvalUsd: roundToUsd(xvalUsd), floorUsd: roundToUsd(Math.max(0, floorUsd - shockedUsd)),
    normProvUsd: roundToUsd(normProvUsd), balancesUsd: balancesUsd as Usd,
    priceUsdPerShare: priceUsdPerShare as UsdPerShare, underDealCovenants: bank.dealOffer?.covenantStatus === "pending",
  };
}

/** The price model's state after a month's `valuation`: the two histories each carry this month, the market multiple rides on the breakdown itself. */
export function advancedPriceState(bank: BankState, v: ValuationBreakdown, params: ValuationParams): Pick<BankState, "valuation" | "rotceHistoryRate" | "balanceHistoryUsd"> {
  const { rotceHistoryMonths: rotceMonths, growthLookbackMonths: balanceMonths } = params.priceModel;
  return {
    valuation: v,
    rotceHistoryRate: [...(bank.rotceHistoryRate ?? []).slice(-(rotceMonths - 1)), v.rotceCoreRate],
    balanceHistoryUsd: [...(bank.balanceHistoryUsd ?? []).slice(-(balanceMonths - 1)), v.balancesUsd ?? loansAndDepositsUsd(bank)],
  };
}
